import { DisabledOptionInfo } from 'components/dropdown/Dropdown';
import {
    buildDisabledOptions,
    getValidationUpdateIntervalMs,
} from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/collectionDurationValidation';
import { V4_SETTINGS_TEXT } from 'components/node_settings/v4SettingsTexts';
import {
    CollectionSetting,
    MAX_COMBINED_NUMBER_OF_SAMPLES,
    UpdateInterval,
} from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { collectionIntervalExceedsUpdateInterval, OptionChild } from 'utils/node';

type LowFrequencyCollectionContext = Readonly<{
    lowFrequencyCollection: CollectionSetting;
    primarySamplingFrequencyHz: number;
    primaryNumberOfSamples: number;
    updateInterval: UpdateInterval;
}>;

function frequencyTooHigh(lfcFrequencyHz: number, primaryFrequencyHz: number): boolean {
    return lfcFrequencyHz >= primaryFrequencyHz;
}

function combinedSamplesExceedLimit(lfcSamples: number, primarySamples: number): boolean {
    return lfcSamples + primarySamples > MAX_COMBINED_NUMBER_OF_SAMPLES;
}

// LFC has its own collection duration (its own frequency/samples), which must fit within the
// update interval in Timed mode - the same rule the backend applies to primary collection,
// reused here for LFC's own values instead of the primary's.
function lfcDurationExceedsInterval(
    lfcSamplingFrequencyHz: number,
    lfcNumberOfSamples: number,
    updateInterval: UpdateInterval,
): boolean {
    return (
        collectionIntervalExceedsUpdateInterval({
            samplingFrequency: lfcSamplingFrequencyHz,
            numberOfSamples: lfcNumberOfSamples,
            updateInterval: getValidationUpdateIntervalMs(updateInterval),
        }) === true
    );
}

export function lowFrequencyCollectionExceedsLimits({
    lowFrequencyCollection,
    primarySamplingFrequencyHz,
    primaryNumberOfSamples,
    updateInterval,
}: LowFrequencyCollectionContext): boolean {
    if (!lowFrequencyCollection.enabled) {
        return false;
    }
    return (
        frequencyTooHigh(
            lowFrequencyCollection.samplingFrequencyHz,
            primarySamplingFrequencyHz,
        ) ||
        combinedSamplesExceedLimit(
            lowFrequencyCollection.numberOfSamples,
            primaryNumberOfSamples,
        ) ||
        lfcDurationExceedsInterval(
            lowFrequencyCollection.samplingFrequencyHz,
            lowFrequencyCollection.numberOfSamples,
            updateInterval,
        )
    );
}

export function getDisabledLfcSamplingFrequencyOptions(
    options: OptionChild<number>[],
    samplingFrequencyHz: number,
): DisabledOptionInfo[] {
    return buildDisabledOptions(
        options,
        (value) => frequencyTooHigh(value, samplingFrequencyHz),
        V4_SETTINGS_TEXT.lowFrequencyCollection.mustBeLowerFrequency,
    );
}

export function getDisabledLfcNumberOfSamplesOptions(
    options: OptionChild<number>[],
    numberOfSamples: number,
): DisabledOptionInfo[] {
    return buildDisabledOptions(
        options,
        (value) => combinedSamplesExceedLimit(value, numberOfSamples),
        V4_SETTINGS_TEXT.lowFrequencyCollection.combinedPrimaryExceeds,
    );
}

// The combined cap is enforced on Save regardless (see lowFrequencyCollectionExceedsLimits),
// but disabling it here too means the primary dropdown itself never offers a value that would
// need LFC to be lowered or turned off to reconcile.
export function getDisabledPrimaryNumberOfSamplesOptions(
    options: OptionChild<number>[],
    lowFrequencyCollection: CollectionSetting,
): DisabledOptionInfo[] {
    if (!lowFrequencyCollection.enabled) {
        return [];
    }
    const lfcSamples = lowFrequencyCollection.numberOfSamples;
    return buildDisabledOptions(
        options,
        (value) => combinedSamplesExceedLimit(lfcSamples, value),
        V4_SETTINGS_TEXT.lowFrequencyCollection.combinedLfcExceeds,
    );
}

// Same reasoning as getDisabledPrimaryNumberOfSamplesOptions, for the frequency-below-primary
// rule instead of the combined-sample cap.
export function getDisabledPrimarySamplingFrequencyOptions(
    options: OptionChild<number>[],
    lowFrequencyCollection: CollectionSetting,
): DisabledOptionInfo[] {
    if (!lowFrequencyCollection.enabled) {
        return [];
    }
    const lfcFrequencyHz = lowFrequencyCollection.samplingFrequencyHz;
    return buildDisabledOptions(
        options,
        (value) => frequencyTooHigh(lfcFrequencyHz, value),
        V4_SETTINGS_TEXT.lowFrequencyCollection.mustBeHigherFrequency,
    );
}
